import { Box, FormControlLabel } from '@mui/material';
import { isAfter, isBefore } from 'date-fns';
import { useFormikContext } from 'formik';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBoolean } from '../../../../../../../hooks/useBoolean';
import { getTwoDaysAgo } from '../../../../../../../utils/utils';
import { RoundSwitch } from '../../../../../../form/RoundSwitch/RoundSwitch';
import { useRmsGanttValue } from '../../../../RmsContext';
import { TurnaroundFormValues } from '../TurnaroundForm.utils';
import { BoardingGateInputField } from './BoardingGateInputField';
import { StyledTimesBox } from './BoardingGateInputs.style';

export const BoardingGateInputs = memo(function BoardingGateInputs() {
    const day = useRmsGanttValue('day');
    const isDayInPast = isBefore(day, getTwoDaysAgo());
    const disabledInputs = isAfter(day, new Date()) || isDayInPast;

    const { values, initialValues, setValues } = useFormikContext<TurnaroundFormValues>();
    const { value: boardingOpen, setValue: setBoardingOpen } = useBoolean(!!values.actualBoardingStart);
    const { value: boardingClose, setValue: setBoardingClose } = useBoolean(!!values.actualBoardingEnd);

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, fieldOpening: boolean) => {
            const now = new Date().getTime();
            const aobt = values.turnaround?.departureFlight?.aobt;
            const newDate = aobt ? Math.min(now, aobt) : now;
            if (fieldOpening) {
                setBoardingOpen(event.target.checked);
                if (event.target.checked) {
                    setValues({ ...values, actualBoardingStart: newDate, updateActualBoardingStart: true });
                } else {
                    setValues({
                        ...values,
                        actualBoardingStart: 0,
                        updateActualBoardingStart: !!initialValues.actualBoardingStart,
                    });
                }
            } else {
                setBoardingClose(event.target.checked);
                if (event.target.checked) {
                    setValues({ ...values, actualBoardingEnd: newDate, updateActualBoardingEnd: true });
                } else {
                    setValues({
                        ...values,
                        actualBoardingEnd: 0,
                        updateActualBoardingEnd: !!initialValues.actualBoardingEnd,
                    });
                }
            }
        },
        [
            initialValues.actualBoardingEnd,
            initialValues.actualBoardingStart,
            setBoardingClose,
            setBoardingOpen,
            setValues,
            values,
        ],
    );

    return (
        <Box width={'100%'} pt={'5px'} borderTop={'1px solid #b6d2e5'}>
            <StyledTimesBox id={'estimated.boarding.times'}>
                <Box width={'40%'}>
                    <BoardingGateInputField
                        field={'estimatedBoardingStart'}
                        updateField={'updateTargetBoardingTimes'}
                        disabled={disabledInputs}
                    />
                </Box>
                &emsp;
                <Box width={'40%'}>
                    <BoardingGateInputField
                        field={'estimatedBoardingEnd'}
                        updateField={'updateTargetBoardingTimes'}
                        disabled={disabledInputs}
                    />
                </Box>
            </StyledTimesBox>
            <StyledTimesBox id={'actual.boarding.buttons'}>
                <Box width={'50%'} display={'flex'} pl={'20px'}>
                    <FormControlLabel
                        control={
                            <RoundSwitch
                                checked={boardingOpen}
                                disabled={boardingClose || disabledInputs}
                                onChange={e => handleChange(e, true)}
                            />
                        }
                        label={
                            boardingOpen ? (
                                <FormattedMessage id={'turnaround.popin.boarding.opened'} />
                            ) : (
                                <FormattedMessage id={'turnaround.popin.boarding.to.open'} />
                            )
                        }
                        disabled={disabledInputs}
                    />
                </Box>
                <Box width={'50%'} display={'flex'} justifyContent={'center'}>
                    <FormControlLabel
                        control={
                            <RoundSwitch
                                checked={boardingClose}
                                disabled={!boardingOpen && !boardingClose}
                                onChange={e => handleChange(e, false)}
                            />
                        }
                        sx={{ marginLeft: '5px' }}
                        label={
                            boardingClose ? (
                                <FormattedMessage id={'turnaround.popin.boarding.closed'} />
                            ) : (
                                <FormattedMessage id={'turnaround.popin.boarding.to.close'} />
                            )
                        }
                        disabled={disabledInputs}
                    />
                </Box>
            </StyledTimesBox>
            <StyledTimesBox id={'actual.boarding.times'}>
                <Box width={'40%'}>
                    <BoardingGateInputField
                        field={'actualBoardingStart'}
                        updateField={'updateActualBoardingStart'}
                        disabled={disabledInputs}
                    />
                </Box>
                &emsp;
                <Box width={'40%'}>
                    <BoardingGateInputField
                        field={'actualBoardingEnd'}
                        updateField={'updateActualBoardingEnd'}
                        disabled={disabledInputs}
                    />
                </Box>
            </StyledTimesBox>
        </Box>
    );
});
